<script setup lang="ts">
import type {
  TypeDownloadableAssetSkeleton,
  TypeDownloadableAssetWithoutUnresolvableLinksResponse,
} from '~/types/contentful'
import type { CustomElementsDependencies } from '~/types/custom-elements'
import MarketingAsset from '~/components/MarketingAsset.vue'

declare global {
  interface Window {
    __WPN_CUSTOM_ELEMENTS__?: CustomElementsDependencies
  }
}

const props = defineProps<{
  entry: string
  debug?: boolean | string
}>()

const isLoading = ref(false)
const data = ref<TypeDownloadableAssetWithoutUnresolvableLinksResponse>()
const isVisible = ref(false)
const hasAccess = ref(false)

const isDebug = computed(() => {
  return (
    props.debug === '' ||
    props.debug === true ||
    props.debug === 'true' ||
    props.debug === '1'
  )
})

const shouldRender = computed(() => {
  return !!data.value && isVisible.value && hasAccess.value
})

async function loadAsset() {
  data.value = undefined
  isVisible.value = false
  hasAccess.value = false

  if (!props.entry) {
    if (isDebug.value) {
      console.log('[DownloadAsset] missing required "entry" prop')
    }
    return
  }
  if (!window.__WPN_CUSTOM_ELEMENTS__) {
    if (isDebug.value) {
      console.log('[DownloadAsset] custom element dependencies are unavailable')
    }
    return
  }

  isLoading.value = true

  try {
    const deps = window.__WPN_CUSTOM_ELEMENTS__
    const locale = deps.getLocale()
    if (isDebug.value) {
      console.log('[DownloadAsset] fetching entry', {
        entryId: props.entry,
        locale,
      })
    }
    const response =
      await deps.client.withoutUnresolvableLinks.getEntries<TypeDownloadableAssetSkeleton>(
        {
          content_type: 'downloadableAsset',
          include: 2,
          limit: 1,
          'sys.id': props.entry,
          locale,
        },
      )

    const entry = response.items[0]
    if (!entry) {
      if (isDebug.value) {
        console.log('[DownloadAsset] no downloadable asset found', {
          entryId: props.entry,
        })
      }
      return
    }

    data.value = entry

    const visibilityDecision = deps.visibilityDecision(
      entry.fields?.visibility,
      { debug: isDebug.value },
    )
    isVisible.value = visibilityDecision.visible === true
    hasAccess.value = deps.userAccessCheck(
      entry.fields?.accessLevel,
      deps.getHighestUserLevel(),
    )
    if (isDebug.value) {
      console.log('[DownloadAsset] gate checks', {
        id: entry.sys.id,
        visibilityDecision,
        isVisible: isVisible.value,
        hasAccess: hasAccess.value,
        shouldRender: shouldRender.value,
      })
    }
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.entry,
  () => {
    loadAsset()
  },
  { immediate: true },
)
</script>

<template>
  <MarketingAsset v-if="shouldRender" :data="data" />
  <div v-else-if="isLoading" class="loading" aria-live="polite" />
</template>

<style lang="scss" scoped>
.loading {
  display: none;
}
</style>
